import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Users, BarChart3, Plus, Trash2, AlertOctagon, Shield } from "lucide-react";

const ROLE_LABELS = { user: "Usuario", agent: "Agente", admin: "Administrador" };

function CompaniesTab() {
  const [companies, setCompanies] = useState([]);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/companies"); setCompanies(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post("/companies", { name: name.trim(), domain: domain.trim() });
      setName(""); setDomain(""); load();
    } catch (er) { setErr(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Borrar esta empresa?")) return;
    try { await api.delete(`/companies/${id}`); load(); }
    catch (er) { setErr(formatApiError(er)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-companies-tab">
      <form onSubmit={create} className="term-panel p-4" data-testid="company-create-form">
        <div className="text-red-500 uppercase text-xs tracking-[0.3em] mb-3 flex items-center gap-2">
          <Building2 size={14} /> Alta de empresa
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_auto] gap-3">
          <div>
            <label className="term-label">Nombre</label>
            <input data-testid="company-name-input" className="term-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp" required />
          </div>
          <div>
            <label className="term-label">Dominio de correo</label>
            <input data-testid="company-domain-input" className="term-input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="acme.com" required />
          </div>
          <div className="self-end">
            <button data-testid="company-create-btn" type="submit" disabled={busy} className="term-btn">
              <Plus size={14} /> {busy ? "Creando..." : "Crear"}
            </button>
          </div>
        </div>
        {err && (
          <div data-testid="company-error" className="flex items-start gap-2 text-red-500 text-xs uppercase tracking-widest mt-3 shake">
            <AlertOctagon size={12} className="mt-0.5" />
            <span className="glitch">{err}</span>
          </div>
        )}
      </form>

      <div className="term-panel p-0">
        <div className="grid grid-cols-[2fr_2fr_1fr] px-4 py-3 border-b border-red-500/20 text-[0.65rem] uppercase tracking-[0.25em] text-red-500">
          <span>Nombre</span><span>Dominio</span><span className="text-right">Acciones</span>
        </div>
        {companies.length === 0 && (
          <div className="p-6 text-neutral-500 text-xs uppercase tracking-[0.2em]">// sin empresas</div>
        )}
        {companies.map((c) => (
          <div key={c.id} data-testid={`company-row-${c.id}`} className="grid grid-cols-[2fr_2fr_1fr] px-4 py-3 border-b border-red-500/10 items-center">
            <span className="text-neutral-100 text-sm">{c.name}</span>
            <span className="text-neutral-400 text-sm">@{c.domain}</span>
            <div className="text-right">
              <button data-testid={`company-delete-${c.id}`} onClick={() => remove(c.id)} className="term-btn-ghost" title="Borrar">
                <Trash2 size={12} /> Borrar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab({ me }) {
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    setErr("");
    try {
      await api.patch(`/users/${id}/role`, { role });
      setUsers((s) => s.map((u) => (u.id === id ? { ...u, role } : u)));
    } catch (e) { setErr(formatApiError(e)); }
  };

const deleteUser = async (id) => {
    if (!window.confirm("¿Seguro que deseas borrar este usuario?")) return;
    setErr("");
    try {
      await api.delete(`/users/${id}`);
      setUsers((s) => s.filter((u) => u.id !== id));
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      {err && (
        <div className="flex items-start gap-2 text-red-500 text-xs uppercase tracking-widest shake" data-testid="users-error">
          <AlertOctagon size={12} className="mt-0.5" />
          <span className="glitch">{err}</span>
        </div>
      )}
      <div className="term-panel p-0">
        <div className="grid grid-cols-[2fr_2fr_1.5fr_1.2fr_1fr] px-4 py-3 border-b border-red-500/20 text-[0.65rem] uppercase tracking-[0.25em] text-red-500">
          <span>Nombre</span><span>Email</span><span>Empresa</span><span>Rol</span><span>Acciones</span>
        </div>
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-[2fr_2fr_1.5fr_1.2fr_1fr] px-4 py-3 border-b border-red-500/10 items-center">
            <span className="text-neutral-100 text-sm">{u.name}</span>
            <span className="text-neutral-400 text-sm truncate">{u.email}</span>
            <span className="text-neutral-500 text-xs uppercase tracking-[0.15em]">{u.company_name || "-"}</span>
            <select
              data-testid={`user-role-select-${u.id}`}
              value={u.role}
              disabled={u.id === me.id}
              onChange={(e) => changeRole(u.id, e.target.value)}
              className="term-input"
              style={{ padding: "4px 8px", fontSize: "0.7rem", width: "auto" }}
            >
              {Object.entries(ROLE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
<button
    disabled={u.id === me?.id}
    onClick={() => deleteUser(u.id)}
    className="term-input hover:bg-red-900/50 text-red-400 border-red-500/50 disabled:opacity-30 disabled:cursor-not-allowed"
    style={{ padding: "4px 8px", fontSize: "0.7rem", width: "auto" }}
  >
    BORRAR
  </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GlobalStatsTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/stats/companies").then((r) => setRows(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);

  const total = rows.reduce((a, r) => a + (r.total || 0), 0);
  const users = rows.reduce((a, r) => a + (r.user_count || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-stats-tab">
      {err && <div className="text-red-500 shake text-xs uppercase tracking-widest">{err}</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="term-panel p-4"><div className="term-label">Empresas</div><div className="text-3xl text-red-500 font-bold tabular-nums" data-testid="global-companies-count">{rows.length}</div></div>
        <div className="term-panel p-4"><div className="term-label">Usuarios totales</div><div className="text-3xl text-red-500 font-bold tabular-nums" data-testid="global-users-count">{users}</div></div>
        <div className="term-panel p-4"><div className="term-label">Bugs totales</div><div className="text-3xl text-red-500 font-bold tabular-nums" data-testid="global-bugs-count">{total}</div></div>
      </div>

      <div className="term-panel p-0">
        <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr] px-4 py-3 border-b border-red-500/20 text-[0.65rem] uppercase tracking-[0.25em] text-red-500">
          <span>Empresa</span>
          <span>Usuarios</span>
          <span>Total</span>
          <span>Brechas</span>
          <span>Infiltraciones</span>
          <span>Parcheados</span>
        </div>
        {rows.map((r) => (
          <div key={r.company_id} data-testid={`company-stat-row-${r.company_id}`} className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr] px-4 py-3 border-b border-red-500/10 items-center">
            <div className="flex flex-col">
              <span className="text-neutral-100 text-sm">{r.company_name}</span>
              <span className="text-neutral-500 text-[0.65rem] uppercase tracking-[0.2em]">@{r.domain}</span>
            </div>
            <span className="text-neutral-100 tabular-nums">{r.user_count}</span>
            <span className="text-red-500 tabular-nums font-bold">{r.total}</span>
            <span className="text-neutral-100 tabular-nums">{r.by_status?.brecha_detectada ?? 0}</span>
            <span className="text-neutral-100 tabular-nums">{r.by_status?.infiltracion_en_curso ?? 0}</span>
            <span className="text-neutral-100 tabular-nums">{r.by_status?.parche_aplicado ?? 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("companies");
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const tabBtn = (id, Icon, label, testid) => (
    <button
      key={id}
      data-testid={testid}
      onClick={() => setTab(id)}
      className={`inline-flex items-center gap-2 px-4 py-2 uppercase text-xs tracking-[0.2em] border ${
        tab === id ? "border-red-500 text-red-500 bg-red-500/5" : "border-transparent text-neutral-400 hover:text-red-500 hover:border-red-500/40"
      }`}
    >
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8" data-testid="admin-panel">
      <div className="flex items-center gap-2 text-red-500 text-xs uppercase tracking-[0.3em] mb-2">
        <Shield size={14} /> // panel_administrativo
      </div>
      <h1 className="text-4xl md:text-5xl uppercase tracking-tighter font-bold mb-8">
        Admin<span className="text-red-500">.</span>panel
      </h1>

      <div className="flex flex-wrap gap-1 border-b border-red-500/20 mb-6">
        {tabBtn("companies", Building2, "Empresas", "admin-tab-companies")}
        {tabBtn("users", Users, "Usuarios", "admin-tab-users")}
        {tabBtn("stats", BarChart3, "Estadísticas", "admin-tab-stats")}
      </div>

      {tab === "companies" && <CompaniesTab />}
      {tab === "users" && <UsersTab me={user} />}
      {tab === "stats" && <GlobalStatsTab />}
    </div>
  );
}
